import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback
} from 'react';
import PropTypes from 'prop-types';
import { getCurrentMonthStartDateTime } from 'src/utils/format-time';

const DashboardFiltersContext = createContext(null);

export function DashboardFiltersProvider({ children }) {
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectedStartTime, setSelectedStartTime] = useState(getCurrentMonthStartDateTime());
  const [selectedEndTime, setSelectedEndTime] = useState(new Date());
  const [realTimeState, setRealTimeState] = useState(true);
  const [refreshSeq, setRefreshSeq] = useState(0)
  const [filterCancelledShipments, setCancelledShipmentState] = useState(true);
  
  const previousRealTimeState = useRef(realTimeState);
  const previousFilterCancelledShipment = useRef(filterCancelledShipments);

  const previousDevicesKeyRef = useRef(
    Array.isArray(selectedDevices) ? [...selectedDevices].sort().join('|') : ''
  );
  const previousStartKeyRef = useRef(
    selectedStartTime ? new Date(selectedStartTime).toISOString() : ''
  );

  const triggerRefresh = useCallback(() => {
    setRefreshSeq((s) => s + 1);
  }, []);

  useEffect(() => {
      if (!previousRealTimeState.current && realTimeState) {
        setRefreshSeq((s) => s + 1);
      }  
      previousRealTimeState.current = realTimeState;
    }, [realTimeState]);
    
  useEffect(() => {
    if (previousFilterCancelledShipment.current !== filterCancelledShipments) {
      setRefreshSeq((s) => s + 1);
      previousFilterCancelledShipment.current = filterCancelledShipments;
    }
  }, [filterCancelledShipments]);
    
  useEffect(() => {
    const devicesKey = Array.isArray(selectedDevices) ? [...selectedDevices].sort().join('|') : '';
    const startKey = selectedStartTime ? new Date(selectedStartTime).toISOString() : '';
  
    if (previousDevicesKeyRef.current !== devicesKey || previousStartKeyRef.current !== startKey) {
      setRefreshSeq((s) => s + 1);
      previousDevicesKeyRef.current = devicesKey;
      previousStartKeyRef.current = startKey;
    }
  }, [selectedDevices, selectedStartTime]);

  const selectedParams = useMemo(
    () => ({
      __rt: realTimeState,
      params: {
        start_time: selectedStartTime,
        end_time: selectedEndTime,
        devices: selectedDevices,
        include_cancelled: filterCancelledShipments,
      },
    }),
    [
      selectedStartTime,
      selectedEndTime,
      selectedDevices,
      filterCancelledShipments,
      realTimeState,
    ]
  );

  const devicesKey = useMemo(() => {
    if (!Array.isArray(selectedDevices) || selectedDevices.length === 0) return '';
    return [...selectedDevices].sort().join('|');
  }, [selectedDevices]);

  const contextKey = useMemo(() => {
    const startKey = selectedStartTime ? new Date(selectedStartTime).toISOString() : '';

    return [
      `d:${devicesKey}`,
      `s:${startKey}`,
      `c:${filterCancelledShipments ? 1 : 0}`,
    ].join(';');
  }, [devicesKey, selectedStartTime, filterCancelledShipments]);

  useEffect(() => {
    if (!realTimeState) return undefined;
    
    const id = setInterval(() => {
      const now = new Date();
      setSelectedEndTime(now);
      setRefreshSeq((s) => s + 1);
    }, 60_000);
    
    return () => clearInterval(id);
  }, [realTimeState]);

  const value = useMemo(
    () => ({
      contextKey,
      selectedDevices,
      setSelectedDevices,
      selectedStartTime,
      setSelectedStartTime,
      selectedEndTime,
      setSelectedEndTime,
      realTimeState,
      setRealTimeState,
      filterCancelledShipments,
      setCancelledShipmentState,
      triggerRefresh,
      refreshSeq,
      selectedParams,
    }),
    [
      contextKey,
      selectedDevices,
      selectedStartTime,
      selectedEndTime,
      realTimeState,
      filterCancelledShipments,
      triggerRefresh,
      refreshSeq,
      selectedParams,
    ]
  );

  return (
    <DashboardFiltersContext.Provider value={value}>
      {children}
    </DashboardFiltersContext.Provider>
  );
}

DashboardFiltersProvider.propTypes = {
  children: PropTypes.node,
};

// Provider já montado acima (rota do dashboard) ou null — o layout só cria um se faltar.
export function useOptionalDashboardFilters() {
  return useContext(DashboardFiltersContext);
}

export function useDashboardFilters() {
  const ctx = useContext(DashboardFiltersContext);
  if (!ctx) {
    throw new Error('useDashboardFilters must be used inside DashboardFiltersProvider');
  }
  return ctx;
}